/**
 * ZIP 批量下载的打包等待提示 —— 记录页 / 详情页共用一份实现
 *
 * 为什么共用：记录页行内「下载」与详情页「批量下载」打的是同一个
 * /material/batch/download（契约 §3.4），等待体验（右下角假进度通知 + 隐藏 iframe 落盘）
 * 必须完全一致；两页各写一份必然漂移（详情页契约 §11.8 的实测结论就差点只修在一页）。
 *
 * 🔴 为什么用隐藏 iframe 落盘而不是 <a target=_blank>（完整实测矩阵见契约 §11.8）：
 *   新开标签必须有「当前有效」的用户手势（transient user activation，只有几秒）。
 *   5 码时取链只要 0.7s，手势还在 → 能下；100 码时要 20~21s，签名链到手时手势已过期
 *   → Chrome 静默丢掉那个新标签（接口 200、链有效，但磁盘上什么都没有）。
 *   也不用「同标签 <a>」：签名链失效时 COS 回 403，会把 SPA 整个页面导航走、状态全丢。
 *
 * 假进度规则：服务端不回真实进度，只能按时间渐近模拟（τ=12s，逼近 90% 后停住），
 * 真拿到签名链那一刻才补到 100% —— 不拿「看起来快完了」骗运营。
 */
import React, { useEffect, useRef } from 'react';
import { Progress, notification } from 'antd';
import { TEXT, ZIP_ETA_SECONDS } from './constants';

/** 打包等待提示的参数（单点，别散在组件里） */
export const ZIP_NOTICE = {
  /** 固定 key：每秒用同一个 key 刷新内容，不堆卡片 */
  KEY: 'material-zip-download',
  /** 假进度上限：服务端不回进度，只有拿到签名链那一刻才补到 100%，不谎报完成 */
  FAKE_MAX_PERCENT: 90,
  /** 假进度时间常数（ms）：τ=12s 时 20s 处约 74%，100 码不会提前撞顶 */
  FAKE_TAU_MS: 12000,
  /** 心跳间隔（ms）：刷新已等待秒数与假进度 */
  TICK_MS: 1000,
  /**
   * 隐藏 iframe 的存活时间（ms）：创建它时 ZIP 已在服务端打完，传输实测 0.22s / 382KB；
   * 但删太早会截断在飞请求，所以留足余量再回收（慢网 / 更大体积也不会被自己掉断）。
   */
  FRAME_TTL_MS: 30000,
};

/**
 * 用隐藏 iframe 触发 ZIP 落盘（契约 §11.8，理由见文件头注释）。
 * 文件名由服务端 Content-Disposition 定，前端不猜也不重复命名。
 * @param {string} url 现取的 zipDownloadUrl（10 分钟签名链，不得缓存复用）
 */
export const triggerZipDownload = (url) => {
  if (!url) {
    return;
  }
  const frame = document.createElement('iframe');
  frame.style.display = 'none';
  frame.setAttribute('aria-hidden', 'true');
  frame.src = url;
  document.body.appendChild(frame);
  window.setTimeout(() => {
    if (frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }
  }, ZIP_NOTICE.FRAME_TTL_MS);
};

/**
 * 右下角「打包中」等待通知卡。
 * start(count) 开卡并每秒心跳刷新假进度；finish(text) 补到 100%；stop() 收卡清定时器。
 * 结果播报仍由页面自己的 message.* toast 负责（文案不动），所以结束时直接 close，
 * 不留两条成功提示。
 * @return {{start: Function, finish: Function, stop: Function}}
 */
export const useZipNotice = () => {
  const timerRef = useRef(null);

  const push = (percent, text) => {
    notification.open({
      key: ZIP_NOTICE.KEY,
      message: TEXT.ZIP_NOTICE_TITLE,
      description: (
        <div>
          <Progress
            percent={Math.round(percent)}
            status="active"
            showInfo={false}
            strokeWidth={6}
          />
          <div style={{ color: '#999', fontSize: 12, marginTop: 2 }}>
            {text}
          </div>
        </div>
      ),
      duration: 0,
      placement: 'bottomRight',
    });
  };

  /** @param {number} [count] 本次打包张数；拿不到（如服务端未回计数）时只报已等待时长 */
  const start = (count) => {
    const startedAt = Date.now();
    const tick = () => {
      const elapsedMs = Date.now() - startedAt;
      // 渐近假进度：服务端没有真实进度可报，所以只逼近 90% 后就不动，
      // 真拿到签名链时才补到 100%（不拿「看起来快完了」骗运营）。
      const percent =
        ZIP_NOTICE.FAKE_MAX_PERCENT *
        (1 - Math.exp(-elapsedMs / ZIP_NOTICE.FAKE_TAU_MS));
      const waited = `已等待 ${Math.floor(elapsedMs / 1000)} 秒 · ${
        TEXT.ZIP_NOTICE_HINT
      }`;
      push(
        percent,
        count
          ? `${count} 张 · 预计约 ${ZIP_ETA_SECONDS(count)} 秒 · ${waited}`
          : waited,
      );
    };
    tick();
    timerRef.current = window.setInterval(tick, ZIP_NOTICE.TICK_MS);
  };

  /** 签名链已到手：假进度撞到 100%，让等待提示与「已交给浏览器」这个事实对齐 */
  const finish = (text) => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    push(100, text);
  };

  const stop = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    notification.close(ZIP_NOTICE.KEY);
  };

  // 卸载兜底：中途离开页面时，不能让「打包中」的卡片永久挂在右下角、定时器永久跑。
  useEffect(() => stop, []);

  return { start, finish, stop };
};
